import { ref, computed } from 'vue';
import store from '@/store';
import { resolveTipoReserva } from '@/utils/reservas/resolveTipoReserva';

/**
 * Composable de dominio para la gestión de reservas genéricas (admin/backend).
 *
 * Encapsula:
 *  - Carga de sesión y sus entradas
 *  - Carga del listado de reservas
 *  - Stock derivado por precio (stockPorPrecioId, stockParaFila)
 *  - Estado semántico y acciones de negocio por reserva
 *  - Acciones: crear, borrar, reenviar email, navegar a pedido
 *  - Gestión del formulario de creación (precios, filas)
 *
 * La vista sólo consume refs/computed/handlers y llama a $modal externamente.
 *
 * @param {object} options
 * @param {string|number} options.eventoId
 * @param {string|number} options.sesionId
 * @param {object} options.config  - instancia de Vue config con getVuexAlias()
 * @param {object} options.toast   - instancia de this.$toast
 * @param {string} options.urlGracias - URL base para construir el enlace público
 * @param {object} options.router - instancia de vue-router para navegación interna
 */
export function useReservasGenericas({ eventoId, sesionId, config, toast, urlGracias, router }) {
    const DOMAIN_ERROR_MESSAGES = {
        'missing-price': 'Selecciona un tipo de entrada válido antes de crear la reserva.',
        'unknown-price': 'El tipo de entrada seleccionado ya no está disponible en esta sesión.',
        'stock-exceeded': 'La cantidad seleccionada supera el stock disponible para algún precio.',
        'cupo-excedido': 'La suma de entradas ({suma}) supera el cupo compartido ({cupo}).',
    };

    // ── ESTADO ────────────────────────────────────────────────────────────────
    const sesion = ref(null);
    const entradas = ref([]);
    const reservas = ref([]);
    const reservaSeleccionada = ref(null);
    const loadingSesion = ref(true);
    const loading = ref(false);
    const loadingCrear = ref(false);

    const currentPage = ref(1);
    const perPage = ref(10);

    const _formDefecto = () => ({
        email: '',
        localizador: '',
        descuento: 0,
        comprar_completa: false,
        precios: [{ precio_id: '', units_total: 1 }],
    });

    const form = ref(_formDefecto());

    function normalizePrecioId(precioId) {
        if (precioId === null || precioId === undefined) {
            return null;
        }

        const normalizedPrecioId = String(precioId).trim();

        return normalizedPrecioId !== '' ? normalizedPrecioId : null;
    }

    function getEntradaStock(entrada) {
        return Math.max(0, Number(entrada?.units || 0) - Number(entrada?.en_sesion || 0) - Number(entrada?.reservadas || 0));
    }

    function getEntradaByPrecioId(precioId) {
        const normalizedPrecioId = normalizePrecioId(precioId);

        if (!normalizedPrecioId) {
            return null;
        }

        return entradas.value.find(entrada => normalizePrecioId(entrada?.id) === normalizedPrecioId) || null;
    }

    function normalizePrecioRows(rows = []) {
        const aggregatedByPrecioId = {};
        const aggregatedRows = [];

        for (const fila of rows) {
            const normalizedPrecioId = normalizePrecioId(fila?.precio_id);

            if (!normalizedPrecioId) {
                return {
                    rows: [],
                    error: 'missing-price',
                };
            }

            const entrada = getEntradaByPrecioId(normalizedPrecioId);

            if (!entrada) {
                return {
                    rows: [],
                    error: 'unknown-price',
                };
            }

            const canonicalPrecioId = normalizePrecioId(entrada.id);
            const normalizedUnitsTotal = Number(fila?.units_total);
            const unitsTotal = Number.isFinite(normalizedUnitsTotal) ? normalizedUnitsTotal : 0;

            if (!aggregatedByPrecioId[canonicalPrecioId]) {
                aggregatedByPrecioId[canonicalPrecioId] = {
                    precio_id: entrada.id,
                    units_total: 0,
                    stock: getEntradaStock(entrada),
                };
                aggregatedRows.push(aggregatedByPrecioId[canonicalPrecioId]);
            }

            aggregatedByPrecioId[canonicalPrecioId].units_total += unitsTotal;
        }

        const stockExceeded = aggregatedRows.some(fila => fila.units_total > fila.stock);

        return {
            rows: aggregatedRows.map(({ stock, ...fila }) => fila),
            error: stockExceeded ? 'stock-exceeded' : null,
        };
    }

    function notifyDomainError(errorCode) {
        if (!errorCode) {
            return;
        }

        toast.error(DOMAIN_ERROR_MESSAGES[errorCode] || 'Error al crear la reserva');
    }

    // ── COMPUTED: STOCK ────────────────────────────────────────────────────────

    // Module-level closure flag: the failsafe `console.error` fires once per
    // composable instance, even across reactive updates of `sesion`. (REQ-SC-RES-04)
    let hasLoggedDrift = false;

    /**
     * Cupo compartido a nivel de sesión (sin numerar).
     * Devuelve `null` cuando la sesión NO usa stock compartido (branch off),
     * `0` cuando hay drift de payload (failsafe, REQ-SC-RES-04), o el cup
     * positivo cuando todo está bien (REQ-SC-RES-01).
     */
    const stockCompartidoSesion = computed(() => {
        if (!sesion.value || sesion.value.stock_compartido !== true) {
            return null;
        }

        const cup = Number(sesion.value.cupo_disponible);

        if (!Number.isFinite(cup) || cup < 0) {
            if (process.env.NODE_ENV !== 'production' && !hasLoggedDrift) {
                console.error('[reservas] stock_compartido=true sin cupo_disponible — payload drift');
                hasLoggedDrift = true;
            }
            return 0;
        }

        return cup;
    });

    /**
     * Mapa { precio_id → stock_disponible } derivado de las entradas de la sesión.
     * stock = max(0, units - en_sesion - reservadas)
     */
    const stockPorPrecioId = computed(() => {
        return entradas.value.reduce((acc, entrada) => {
            const normalizedPrecioId = normalizePrecioId(entrada?.id);

            if (!normalizedPrecioId) {
                return acc;
            }

            acc[normalizedPrecioId] = getEntradaStock(entrada);
            return acc;
        }, {});
    });

    /**
     * Devuelve el stock disponible para la fila del formulario en la posición `index`.
     *
     * Contrato:
     *   - `stock_compartido` activo: retorna SIEMPRE un número: el cup compartido
     *     menos `suma_otras_filas`, opcionalmente recortado al stock por entrada
     *     si ya hay un precio seleccionado. Esto garantiza que el cap se conoce
     *     desde la creación de la fila, ANTES de que el usuario elija un
     *     `precio_id` (REQ-SC-RES-01 / REQ-SC-CAP-AGG-01).
     *   - `stock_compartido` OFF (legacy): retorna `null` cuando no hay precio
     *     seleccionado o no se conoce el stock por entrada; retorna el stock por
     *     entrada cuando sí está disponible (REQ-SC-RES-03).
     *
     * La invariante: la SUMA de `units_total` entre filas no puede superar el
     * cup compartido. Si el cup no está disponible, el failsafe devuelve 0
     * (REQ-SC-RES-04).
     */
    const stockParaFila = computed(() => index => {
        const fila = form.value.precios[index];

        if (!fila) {
            return null;
        }

        const cupCompartido = stockCompartidoSesion.value;

        // Stock compartido branch — se consulta ANTES del lookup de entrada
        // para que el cap sea calculable desde la creación de la fila, incluso
        // sin un precio seleccionado (REQ-SC-RES-07).
        if (cupCompartido !== null && cupCompartido !== undefined) {
            const sumaOtras = form.value.precios.reduce((acc, f, i) => {
                if (i === index) {
                    return acc;
                }
                return acc + (Number(f.units_total) || 0);
            }, 0);

            const restante = Math.max(0, cupCompartido - sumaOtras);

            const entrada = getEntradaByPrecioId(fila.precio_id);

            if (!entrada) {
                return restante;
            }

            const normalizedPrecioId = normalizePrecioId(entrada.id);
            const stock = stockPorPrecioId.value[normalizedPrecioId];

            if (stock === undefined || stock === null) {
                return restante;
            }

            return Math.min(stock, restante);
        }

        // Legacy branch (stock_compartido OFF): per-entrada stock only.
        const entrada = getEntradaByPrecioId(fila.precio_id);

        if (!entrada) {
            return null;
        }

        const stock = stockPorPrecioId.value[normalizePrecioId(entrada.id)];

        if (stock === undefined || stock === null) {
            return null;
        }

        return stock;
    });

    // ── COMPUTED: PAGINACIÓN ─────────────────────────────────────────────────

    const totalReservas = computed(() => reservas.value.length);

    const paginatedReservas = computed(() => {
        const start = (currentPage.value - 1) * perPage.value;
        const end = start + perPage.value;
        return reservas.value.slice(start, end);
    });

    // ── HELPERS DE ESTADO / ACCIONES ──────────────────────────────────────────

    /**
     * Devuelve una clave semántica del estado.
     * @param {number} status
     * @returns {string}
     */
    function getReservaStatusKey(status) {
        const statuses = {
            0: 'pending',
            1: 'completed',
            2: 'cancelled',
            3: 'processing',
        };

        return statuses[status] || 'unknown';
    }

    /**
     * Extrae el número de pedido asociado a una reserva completada.
     * Regla actual: usar el primer pedido disponible del payload.
     * @param {object} reserva
     * @returns {string|null}
     */
    function getReservaPedidoNumber(reserva) {
        const pedidoId = reserva?.pedidos?.[0]?.id;

        if (pedidoId === undefined || pedidoId === null || pedidoId === '') {
            return null;
        }

        return String(pedidoId);
    }

    /**
     * Navega al listado de ventas de la sesión filtrado por número de pedido.
     * @param {object} reserva
     */
    function verPedido(reserva) {
        const pedidoId = getReservaPedidoNumber(reserva);

        if (!pedidoId || !router) {
            return;
        }

        router.push({
            name: 'back.events.ventas',
            params: {
                evento_id: String(eventoId),
                sesion_id: String(sesionId),
            },
            query: {
                n_pedido: pedidoId,
            },
        });
    }

    /**
     * Devuelve las intenciones de acciones disponibles para una reserva.
     * @param {object} reserva
     * @param {object} handlers
     * @returns {Array}
     */
    function getReservaActionIntents(reserva, handlers = {}) {
        const intents = [];

        if (reserva?.status == 0 || reserva?.status == 3) {
            intents.push('copy-link', 'resend-email');

            if (typeof handlers.onDelete === 'function') {
                intents.push('delete');
            }
        }

        if (reserva?.status == 1 && getReservaPedidoNumber(reserva)) {
            intents.push('view-order');
        }

        return intents;
    }

    // ── API CALLS ─────────────────────────────────────────────────────────────

    /**
     * Carga la sesión y sus entradas disponibles desde el backend.
     * Filtra entradas de tipo "Invitación Protocolo".
     */
    function getSesion() {
        loadingSesion.value = true;

        const params = {
            filters: {},
            evento_id: eventoId,
            sesion_id: sesionId,
        };

        return store.dispatch(config.getVuexAlias('back.events.sesiones.show'), params)
            .then(res => {
                sesion.value = res.data.data;
                entradas.value = (res.data.data.entradas || []).filter(
                    e => e.descripcion !== 'Invitación Protocolo'
                );
            })
            .catch(err => {
                if (process.env.NODE_ENV !== 'production') {
                    console.error('Error al cargar la sesión', err);
                }
            })
            .finally(() => {
                loadingSesion.value = false;
            });
    }

    /**
     * Carga el listado de reservas genéricas de la sesión.
     */
    function getReservas() {
        loading.value = true;

        const params = {
            filters: {},
            evento_id: eventoId,
            sesion_id: sesionId,
        };

        return store.dispatch(config.getVuexAlias('back.events.sesiones.reservas.index'), params)
            .then(res => {
                const data = res.data;
                reservas.value = Array.isArray(data) ? data : (data.data || []);
            })
            .catch(err => {
                if (process.env.NODE_ENV !== 'production') {
                    console.error('Error al cargar las reservas', err);
                }
                toast.error('Error al cargar las reservas');
            })
            .finally(() => {
                loading.value = false;
            });
    }

    // ── ACCIONES DE FORMULARIO ────────────────────────────────────────────────

    /** Añade una fila vacía al formulario de precios. */
    function agregarFila() {
        form.value.precios.push({ precio_id: '', units_total: 1 });
    }

    /** Elimina la fila de precios en la posición `index`. */
    function quitarFila(index) {
        form.value.precios.splice(index, 1);
    }

    /**
     * Reacciona al cambio de precio en una fila:
     * clampea las unidades al stock disponible si lo supera.
     * Devuelve la nueva fila si se clampea (para que la vista haga $set), o null.
     *
     * @param {number} index
     * @returns {{ fila: object, index: number } | null}
     */
    function onPrecioChange(index) {
        const fila = form.value.precios[index];

        if (!fila) {
            return null;
        }

        const entrada = getEntradaByPrecioId(fila.precio_id);

        if (!entrada) {
            return {
                fila: {
                    ...fila,
                    precio_id: '',
                    units_total: 1,
                },
                index,
            };
        }

        const normalizedPrecioId = normalizePrecioId(entrada.id);
        const stock = stockPorPrecioId.value[normalizedPrecioId];
        const shouldClampUnits = stock !== undefined && fila.units_total > stock;
        const shouldCanonicalizePrecioId = fila.precio_id !== entrada.id;

        if (shouldClampUnits || shouldCanonicalizePrecioId) {
            const nuevaFila = {
                ...fila,
                precio_id: entrada.id,
                units_total: shouldClampUnits ? (stock > 0 ? stock : 1) : fila.units_total,
            };
            return { fila: nuevaFila, index };
        }

        return null;
    }

    /** Reinicia el formulario a sus valores por defecto y resetea el observer de VeeValidate (si se pasa). */
    function resetForm(observerRef = null) {
        form.value = _formDefecto();
        if (observerRef && observerRef.reset) {
            observerRef.reset();
        }
    }

    // ── ACCIONES DE RESERVA ───────────────────────────────────────────────────

    /**
     * Valida stock y construye el payload para crear una nueva reserva genérica.
     * Retorna el payload si es válido, o null si hay error de stock.
     *
     * El llamante (vista) debe validar VeeValidate antes de llamar a esta función.
     *
     * @returns {object|null} params para el dispatch, o null si hay exceso de stock
     */
    function buildCrearPayload() {
        const { rows, error } = normalizePrecioRows(form.value.precios);

        if (error) {
            notifyDomainError(error);
            return null;
        }

        // REQ-SC-CAP-AGG-VAL-01: enforce the aggregate cap BEFORE returning the
        // payload. The per-row caps (max_value, onCantidadInput) only protect a
        // single row — a user can still bypass them by spreading units across
        // multiple rows. The backend is the final source of truth, but we fail
        // fast in the client with a domain-specific toast.
        const suma = rows.reduce((acc, fila) => acc + Number(fila.units_total || 0), 0);
        const cupCompartido = stockCompartidoSesion.value;

        if (cupCompartido !== null && suma > cupCompartido) {
            toast.error(
                DOMAIN_ERROR_MESSAGES['cupo-excedido']
                    .replace('{suma}', String(suma))
                    .replace('{cupo}', String(cupCompartido))
            );
            return null;
        }

        const params = {
            filters: {},
            evento_id: eventoId,
            sesion_id: sesionId,
            email: form.value.email,
            descuento: Number(form.value.descuento),
            comprar_completa: Boolean(form.value.comprar_completa),
            precios: rows.map(fila => ({
                precio_id: fila.precio_id,
                units_total: fila.units_total,
            })),
        };

        if (form.value.localizador && form.value.localizador.trim() !== '') {
            params.localizador = form.value.localizador.trim();
        }

        params.tipo_reserva = resolveTipoReserva(params);

        return params;
    }

    /**
     * Envía la petición de creación de reserva.
     * Debe llamarse sólo después de que VeeValidate haya validado el formulario.
     *
     * @param {Function} onSuccess - callback cuando la reserva se crea correctamente
     */
    async function crearReserva(onSuccess) {
        const params = buildCrearPayload();
        if (!params) return;

        loadingCrear.value = true;

        try {
            const res = await store.dispatch(config.getVuexAlias('back.events.sesiones.reservas.genericas.create'), params);

            await Promise.all([
                getReservas(),
                getSesion(),
            ]);

            toast.success(res.data.message || 'Reserva creada correctamente');

            if (onSuccess) onSuccess(res);
        } catch (err) {
            const msg = err.response && err.response.data && err.response.data.message
                ? err.response.data.message
                : 'Error al crear la reserva';
            toast.error(msg);
        } finally {
            loadingCrear.value = false;
        }
    }

    /**
     * Selecciona una reserva para la acción de borrado.
     * @param {object} reserva
     */
    function seleccionarParaBorrar(reserva) {
        reservaSeleccionada.value = reserva;
    }

    /**
     * Limpia la reserva seleccionada y recarga el listado.
     */
    function limpiarSeleccionBorrar() {
        reservaSeleccionada.value = null;
        getReservas();
        getSesion();
    }

    /**
     * Confirma y ejecuta el borrado de la reserva seleccionada.
     * @param {Function} onClose - callback para cerrar el modal tras la operación
     */
    function confirmarBorrar(onClose) {
        if (!reservaSeleccionada.value) return;

        const params = {
            filters: {},
            evento_id: eventoId,
            sesion_id: sesionId,
            reserva_id: reservaSeleccionada.value.id,
        };

        store.dispatch(config.getVuexAlias('back.events.sesiones.reservas.delete'), params)
            .then(() => {
                toast.success('Reserva eliminada correctamente');
                if (onClose) onClose();
            })
            .catch(err => {
                const msg = err.response && err.response.data && err.response.data.message
                    ? err.response.data.message
                    : 'Error al eliminar la reserva';
                toast.error(msg);
                if (onClose) onClose();
            });
    }

    /**
     * Reenvía el email de confirmación de una reserva.
     * @param {object} reserva
     */
    function reenviarEmail(reserva) {
        const params = {
            filters: {},
            evento_id: eventoId,
            sesion_id: sesionId,
            reserva_id: reserva.id,
        };

        store.dispatch(config.getVuexAlias('back.events.sesiones.reservas.enviar-email'), params)
            .then(() => {
                toast.success('Email reenviado correctamente');
            })
            .catch(() => {
                toast.error('Error al reenviar el email');
            });
    }

    /**
     * Construye el enlace público de una reserva.
     * @param {object} reserva
     * @returns {string}
     */
    function buildReservaLink(reserva) {
        return `${urlGracias}reservas/${reserva.hash}`;
    }

    // ── INICIALIZACIÓN ────────────────────────────────────────────────────────

    getSesion();
    getReservas();

    return {
        // Estado
        sesion,
        entradas,
        reservas,
        reservaSeleccionada,
        loadingSesion,
        loading,
        loadingCrear,
        form,
        // Computed
        stockCompartidoSesion,
        stockPorPrecioId,
        stockParaFila,
        normalizePrecioId,
        // Paginación
        currentPage,
        perPage,
        paginatedReservas,
        totalReservas,
        getEntradaByPrecioId,
        normalizePrecioRows,
        // Helpers
        getReservaStatusKey,
        getReservaPedidoNumber,
        getReservaActionIntents,
        buildReservaLink,
        // Mutaciones de formulario
        agregarFila,
        quitarFila,
        onPrecioChange,
        resetForm,
        buildCrearPayload,
        // Acciones de reserva
        crearReserva,
        seleccionarParaBorrar,
        limpiarSeleccionBorrar,
        confirmarBorrar,
        reenviarEmail,
        verPedido,
    };
}
